import pickBy from 'lodash/pickBy'
import {
  GeckoCircumferentialWeld,
  GeckoCircumferentialWeldJSON,
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoPipeIntegralSupport,
  GeckoPipeIntegralSupportJSON,
  GeckoPipeNonIntegralSupport,
  GeckoPipeNonIntegralSupportJSON,
  GeckoPipeWrap,
  GeckoPipeWrapJSON,
  GeckoSeamWeld,
  GeckoSeamWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { mergeMaps } from 'utils/mergeMaps'
import {
  parseBoolean,
  parseDate,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

export enum GeckoPipeFlowDirection {
  Positive = 'positive',
  Negative = 'negative',
  Unknown = 'unknown',
}

type PipeSectionSupportedFeatures =
  | GeckoFixedSensorJSON
  | GeckoNozzleJSON
  | GeckoPipeNonIntegralSupportJSON
  | GeckoPipeIntegralSupportJSON
  | GeckoCircumferentialWeldJSON
  | GeckoSeamWeldJSON
  | GeckoPipeWrapJSON
  | GeckoPlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export enum PipeOrientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export type PipeSectionAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PipeSection
  radius: number // Outer radius
  inner_radius?: number // Optional, most cases of radius will be outer
  length: number
  orientation?: PipeOrientation
  material: string | undefined
  installation_date: string | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined
  allowable_stress: number | undefined
  flow_direction: GeckoPipeFlowDirection | undefined
  clock_bearing_rotation: number | undefined
  features: Record<string, PipeSectionSupportedFeatures>
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  design_pressure: number | undefined
  yield_strength: number | undefined
  compass_orientation?: number
  design_temp: number | undefined
  pressure_coefficient: number | undefined
  weld_strength_reduction: number | undefined
  tsl: number | undefined
  corrosion_on_inner_surface: boolean | undefined
}

export const PipeSectionAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.FixedSensor,
  GeckoFeatureModelType.PipeNonIntegralSupport,
  GeckoFeatureModelType.PipeIntegralSupport,
  GeckoFeatureModelType.CircumferentialWeld,
  GeckoFeatureModelType.SeamWeld,
  GeckoFeatureModelType.PipeWrap,
]

export const NewPipeSectionAssetModelJSON = {
  name: 'New Pipe Section',
  type: GeckoComponentModelType.PipeSection,
  radius: 0,
  inner_radius: undefined,
  length: 0,
  material: undefined,
  installation_date: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,
  allowable_stress: undefined,
  orientation: undefined,
  flow_direction: undefined,
  clock_bearing_rotation: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  design_pressure: undefined,
  yield_strength: undefined,
  compass_orientation: undefined,
  design_temp: undefined,
  pressure_coefficient: undefined,
  weld_strength_reduction: undefined,
  tsl: undefined,
  corrosion_on_inner_surface: undefined,
  nominal: {},
} as PipeSectionAssetModelJSON

export class GeckoPipeSection extends GeckoEditableComponent {
  type: GeckoComponentModelType.PipeSection
  radius: GeckoMeasurement // Outer radius
  innerRadius: GeckoMeasurement | undefined
  length: GeckoMeasurement
  orientation: PipeOrientation
  material: string | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined
  allowableStress: GeckoMeasurement | undefined
  flowDirection: GeckoPipeFlowDirection | undefined
  clockBearingRotation: number | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  fixedSensors: Map<string, GeckoFixedSensor>
  plates: Map<string, GeckoPlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>
  pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>
  CircumferentialWelds: Map<string, GeckoCircumferentialWeld>
  SeamWelds: Map<string, GeckoSeamWeld>
  pipeWraps: Map<string, GeckoPipeWrap>
  features: Map<
    string,
    | GeckoNozzle
    | GeckoFixedSensor
    | GeckoPlate
    | GeckoVerticalWeld
    | GeckoHorizontalWeld
    | GeckoPipeNonIntegralSupport
    | GeckoPipeIntegralSupport
    | GeckoCircumferentialWeld
    | GeckoSeamWeld
    | GeckoPipeWrap
  >
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  designPressure: GeckoMeasurement | undefined
  compassOrientation: number | undefined
  designTemp: GeckoMeasurement | undefined
  pressureCoefficient: number | undefined
  weldStrengthReduction: number | undefined
  tsl: GeckoMeasurement | undefined
  corrosionOnInnerSurface: boolean | undefined
  /**
   * Material yield strength S_y (intrinsic). Interim: stored directly; the
   * long-term intent (spec §3.1) is to derive it from `material` + `designTemp`.
   * This is the asset-model home for the API 579 t_min / MAWP yield term so the
   * FFS calc no longer depends on the retired Data Gathering step.
   */
  yieldStrength: GeckoMeasurement | undefined

  // Computed property: outer diameter derived from outer radius
  public get outerDiameter(): GeckoMeasurement {
    return this.radius.scale(2)
  }

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    innerRadius: GeckoMeasurement | undefined,
    length: GeckoMeasurement,
    orientation: PipeOrientation,
    material: string | undefined,
    installationDate: Date | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    allowableStress: GeckoMeasurement | undefined,
    flowDirection: GeckoPipeFlowDirection | undefined,
    clockBearingRotation: number | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    plates: Map<string, GeckoPlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>,
    pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>,
    CircumferentialWelds: Map<string, GeckoCircumferentialWeld>,
    SeamWelds: Map<string, GeckoSeamWeld>,
    pipeWraps: Map<string, GeckoPipeWrap>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    designPressure: GeckoMeasurement | undefined,
    compassOrientation: number | undefined,
    designTemp: GeckoMeasurement | undefined,
    pressureCoefficient: number | undefined,
    weldStrengthReduction: number | undefined,
    tsl: GeckoMeasurement | undefined,
    corrosionOnInnerSurface: boolean | undefined,
    yieldStrength: GeckoMeasurement | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.PipeSection
    this.radius = radius
    this.innerRadius = innerRadius
    this.length = length
    this.orientation = orientation
    this.material = material
    this.installationDate = installationDate
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.allowableStress = allowableStress
    this.flowDirection = flowDirection
    this.clockBearingRotation = clockBearingRotation
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.designPressure = designPressure
    this.compassOrientation = compassOrientation
    this.designTemp = designTemp
    this.pressureCoefficient = pressureCoefficient
    this.weldStrengthReduction = weldStrengthReduction
    this.tsl = tsl
    this.corrosionOnInnerSurface = corrosionOnInnerSurface
    this.yieldStrength = yieldStrength

    // Features
    this.nozzles = nozzles
    this.fixedSensors = fixedSensors
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.pipeNonIntegralSupports = pipeNonIntegralSupports
    this.pipeIntegralSupports = pipeIntegralSupports
    this.CircumferentialWelds = CircumferentialWelds
    this.SeamWelds = SeamWelds
    this.pipeWraps = pipeWraps

    this.features = mergeMaps(
      nozzles,
      fixedSensors,
      plates,
      verticalWelds,
      horizontalWelds,
      pipeNonIntegralSupports,
      pipeIntegralSupports,
      CircumferentialWelds,
      SeamWelds,
      pipeWraps
    )
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get pipeNonIntegralSupportsArray() {
    return Array.from(this.pipeNonIntegralSupports.values())
  }

  public get pipeIntegralSupportsArray() {
    return Array.from(this.pipeIntegralSupports.values())
  }

  public get circumferentialWeldsArray() {
    return Array.from(this.CircumferentialWelds.values())
  }

  public get seamWeldsArray() {
    return Array.from(this.SeamWelds.values())
  }

  public get pipeWrapsArray() {
    return Array.from(this.pipeWraps.values())
  }

  public static CreateFromJSON(id: string, json: PipeSectionAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const pipeRadius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const pipeLength = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )

    const orientation = json.orientation
      ? (json.orientation as PipeOrientation)
      : PipeOrientation.Horizontal
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const parsedAllowableStress = parseNumber(json.allowable_stress)
    const allowableStress =
      parsedAllowableStress !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedAllowableStress,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const flowDirection = json.flow_direction as
      | GeckoPipeFlowDirection
      | undefined

    const clockBearingRotation = parseNumber(json.clock_bearing_rotation)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      PipeSectionSupportedFeatures
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childFixedSensorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const childPipeNonIntegralSupportJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeNonIntegralSupport
    })

    const pipeNonIntegralSupports =
      GeckoPipeNonIntegralSupport.CreateMapFromJSON(
        id,
        childPipeNonIntegralSupportJSON as Record<
          string,
          GeckoPipeNonIntegralSupportJSON
        >
      )

    const childPipeIntegralSupportJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeIntegralSupport
    })

    const pipeIntegralSupports = GeckoPipeIntegralSupport.CreateMapFromJSON(
      id,
      childPipeIntegralSupportJSON as Record<
        string,
        GeckoPipeIntegralSupportJSON
      >
    )

    const childCircumferentialWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.CircumferentialWeld
    })

    const CircumferentialWelds = GeckoCircumferentialWeld.CreateMapFromJSON(
      id,
      childCircumferentialWeldJSON as unknown as Record<
        string,
        GeckoCircumferentialWeldJSON
      >,
      componentBounds
    )
    const childSeamWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.SeamWeld
    })

    const SeamWelds = GeckoSeamWeld.CreateMapFromJSON(
      id,
      childSeamWeldJSON as Record<string, GeckoSeamWeldJSON>
    )

    const childPipeWrapJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeWrap
    })

    const pipeWraps = GeckoPipeWrap.CreateMapFromJSON(
      id,
      childPipeWrapJSON as Record<string, GeckoPipeWrapJSON>,
      componentBounds
    )
    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const designPressureJson = json.design_pressure
    const parsedDesignPressure = parseNumber(designPressureJson)
    const designPressure =
      parsedDesignPressure !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedDesignPressure,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined

    const parsedYieldStrength = parseNumber(json.yield_strength)
    const yieldStrength =
      parsedYieldStrength !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedYieldStrength,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined

    const innerRadiusJson = json.inner_radius
    const parsedInnerRadius = parseNumber(innerRadiusJson)
    const innerRadius =
      parsedInnerRadius !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedInnerRadius,
            GeckoMeasurementUnit.Meters
          )
        : undefined

    const parsedDesignTemp = parseNumber(json.design_temp)
    const designTemp =
      parsedDesignTemp !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedDesignTemp,
            GeckoMeasurementUnit.Celsius
          )
        : undefined

    const pressureCoefficient = parseNumber(json.pressure_coefficient)

    const weldStrengthReduction = parseNumber(json.weld_strength_reduction)

    const parsedTsl = parseNumber(json.tsl)
    const tsl =
      parsedTsl !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTsl,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    const corrosionOnInnerSurface = parseBoolean(
      json.corrosion_on_inner_surface
    )

    return new GeckoPipeSection(
      id,
      json,
      name,
      pipeRadius,
      innerRadius,
      pipeLength,
      orientation,
      material,
      installationDate,
      jointEfficiency,
      stressTolerance,
      allowableStress,
      flowDirection,
      clockBearingRotation,
      componentBounds,
      sceneConfig,
      nozzles,
      fixedSensors,
      plates,
      verticalWelds,
      horizontalWelds,
      pipeNonIntegralSupports,
      pipeIntegralSupports,
      CircumferentialWelds,
      SeamWelds,
      pipeWraps,
      wallNominalThickness,
      tmin,
      designPressure,
      compassOrientation,
      designTemp,
      pressureCoefficient,
      weldStrengthReduction,
      tsl,
      corrosionOnInnerSurface,
      yieldStrength
    )
  }

  public asJSON(): PipeSectionAssetModelJSON {
    return generatePipeSection(
      this.name,
      this.radius,
      this.innerRadius,
      this.length,
      this.orientation,
      this.material,
      this.installationDate,
      this.jointEfficiency,
      this.stressTolerance,
      this.allowableStress,
      this.flowDirection,
      this.clockBearingRotation,
      this.wallNominalThickness,
      this.tmin,
      this.designPressure,
      this.compassOrientation,
      this.designTemp,
      this.pressureCoefficient,
      this.weldStrengthReduction,
      this.tsl,
      this.corrosionOnInnerSurface,
      this.yieldStrength,
      this.json as PipeSectionAssetModelJSON
    )
  }
}

const generatePipeSection = (
  name: string,
  radius: GeckoMeasurement,
  innerRadius: GeckoMeasurement | undefined,
  length: GeckoMeasurement,
  orientation: PipeOrientation,
  material: string | undefined,
  installationDate: Date | undefined,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  allowableStress: GeckoMeasurement | undefined,
  flowDirection: GeckoPipeFlowDirection | undefined,
  clockBearingRotation: number | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  designPressure: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  designTemp: GeckoMeasurement | undefined,
  pressureCoefficient: number | undefined,
  weldStrengthReduction: number | undefined,
  tsl: GeckoMeasurement | undefined,
  corrosionOnInnerSurface: boolean | undefined,
  yieldStrength: GeckoMeasurement | undefined,
  otherPipeSectionData: PipeSectionAssetModelJSON = NewPipeSectionAssetModelJSON
): PipeSectionAssetModelJSON => {
  const pipeSectionLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeSectionRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeSectionCircumference = GeckoMeasurement.CreateFromValue(
    pipeSectionRadius * 2 * pi,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedAllowableStress =
    allowableStress !== undefined
      ? allowableStress
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedDesignPressure =
    designPressure !== undefined
      ? designPressure
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedYieldStrength =
    yieldStrength !== undefined
      ? yieldStrength
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInnerRadius =
    innerRadius !== undefined
      ? innerRadius
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedDesignTemp =
    designTemp !== undefined
      ? designTemp
          .convertTo(GeckoMeasurementUnit.Celsius)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tslMeters =
    tsl !== undefined
      ? tsl
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PipeSection,
    radius: pipeSectionRadius,
    inner_radius: parsedInnerRadius,
    length: pipeSectionLength,
    orientation,
    material: material,
    installation_date: parsedInstallationDate,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    allowable_stress: parsedAllowableStress,
    flow_direction: flowDirection,
    clock_bearing_rotation: clockBearingRotation,
    features: otherPipeSectionData.features,
    scene_config: otherPipeSectionData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: pipeSectionCircumference,
      y_end: pipeSectionLength,
    },
    tmin: tminMeters,
    design_pressure: parsedDesignPressure,
    yield_strength: parsedYieldStrength,
    compass_orientation: compassOrientation,
    design_temp: parsedDesignTemp,
    pressure_coefficient: pressureCoefficient,
    weld_strength_reduction: weldStrengthReduction,
    tsl: tslMeters,
    corrosion_on_inner_surface: corrosionOnInnerSurface,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const PipeSectionAssetModelFormFields = (
  pipeSectionJSON: PipeSectionAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Pipe Section Name',
      value: pipeSectionJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Radius (Outer)',
      value: pipeSectionJSON.radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'inner_radius',
      label: 'Inner Radius (Optional)',
      value: pipeSectionJSON.inner_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: pipeSectionJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'orientation',
      label: 'Orientation',
      value: pipeSectionJSON.orientation,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: [
        {
          value: PipeOrientation.Vertical,
          label: 'vertical',
        },
        {
          value: PipeOrientation.Horizontal,
          label: 'horizontal',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: pipeSectionJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(pipeSectionJSON.installation_date, true),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: pipeSectionJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: pipeSectionJSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'allowable_stress',
      label: 'Max Allowable Stress',
      value: pipeSectionJSON.allowable_stress,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'flow_direction',
      label: 'Flow Direction',
      value: pipeSectionJSON.flow_direction,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          value: GeckoPipeFlowDirection.Unknown,
          label: 'Unknown',
        },
        {
          value: GeckoPipeFlowDirection.Positive,
          label: 'Positive',
        },
        {
          value: GeckoPipeFlowDirection.Negative,
          label: 'Negative',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'clock_bearing_rotation',
      label: 'Clock Rotation',
      value: pipeSectionJSON.clock_bearing_rotation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(pipeSectionJSON.nominal.wall_thickness),
    {
      dataKey: 'design_pressure',
      label: 'Design Pressure',
      value: pipeSectionJSON.design_pressure,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'yield_strength',
      label: 'Yield Strength (S_y)',
      value: pipeSectionJSON.yield_strength,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    tminFormField(pipeSectionJSON.tmin),
    compassOrientationFormField(pipeSectionJSON.compass_orientation),
    {
      dataKey: 'design_temp',
      label: 'Design Temperature',
      value: pipeSectionJSON.design_temp,
      unit_magnitude: UnitMagnitude.FahrenheitOrCelsius,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'pressure_coefficient',
      label: 'Pressure Coefficient (Y)',
      value: pipeSectionJSON.pressure_coefficient,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'weld_strength_reduction',
      label: 'Weld Strength Reduction Factor (W)',
      value: pipeSectionJSON.weld_strength_reduction,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tsl',
      label: 'Thickness for Supplemental Loads (t_sl)',
      value: pipeSectionJSON.tsl,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'corrosion_on_inner_surface',
      label: 'Corrosion on Inner Surface',
      value:
        pipeSectionJSON.corrosion_on_inner_surface === undefined
          ? undefined
          : String(pipeSectionJSON.corrosion_on_inner_surface),
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          value: 'true',
          label: 'Inner Surface',
        },
        {
          value: 'false',
          label: 'Outer Surface',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
  ]
}

export const PipeSectionJSONFromFormFields = (
  component: PipeSectionAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedAllowableStress = parseNumber(
    getFormFieldByDataKey(formFields, 'allowable_stress')?.value
  )
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const parsedDesignPressure = parseNumber(
    getFormFieldByDataKey(formFields, 'design_pressure')?.value
  )
  const parsedYieldStrength = parseNumber(
    getFormFieldByDataKey(formFields, 'yield_strength')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const flowDirection = getFormFieldByDataKey(formFields, 'flow_direction')
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  const parsedInnerRadius = parseNumber(
    getFormFieldByDataKey(formFields, 'inner_radius')?.value
  )
  const parsedDesignTemp = parseNumber(
    getFormFieldByDataKey(formFields, 'design_temp')?.value
  )
  const parsedPressureCoefficient = parseNumber(
    getFormFieldByDataKey(formFields, 'pressure_coefficient')?.value
  )
  const parsedWeldStrengthReduction = parseNumber(
    getFormFieldByDataKey(formFields, 'weld_strength_reduction')?.value
  )
  const parsedTsl = parseNumber(
    getFormFieldByDataKey(formFields, 'tsl')?.value
  )
  const corrosionOnInnerSurfaceValue = getFormFieldByDataKey(
    formFields,
    'corrosion_on_inner_surface'
  )?.value

  return generatePipeSection(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    parsedInnerRadius !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedInnerRadius,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'orientation')?.value as PipeOrientation,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parsedJointEfficiency,
    parsedStressTolerance !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedStressTolerance,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    parsedAllowableStress !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedAllowableStress,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    flowDirection?.value === 'undefined'
      ? undefined
      : (flowDirection?.value as GeckoPipeFlowDirection),
    parseNumber(
      getFormFieldByDataKey(formFields, 'clock_bearing_rotation')?.value
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedDesignPressure !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedDesignPressure,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    compassOrientation,
    parsedDesignTemp !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedDesignTemp,
          GeckoMeasurementUnit.Celsius
        )
      : undefined,
    parsedPressureCoefficient,
    parsedWeldStrengthReduction,
    parsedTsl !== undefined
      ? GeckoMeasurement.CreateFromValue(parsedTsl, GeckoMeasurementUnit.Meters)
      : undefined,
    corrosionOnInnerSurfaceValue === 'true'
      ? true
      : corrosionOnInnerSurfaceValue === 'false'
        ? false
        : undefined,
    parsedYieldStrength !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedYieldStrength,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    component
  )
}
